if (!customElements.get('amara-tabs')) {
  customElements.define(
    'amara-tabs',
    class AmaraTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => this.onKeyDown(event, index));
        });
      }

      onKeyDown(event, index) {
        const offset = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: this.tabs.length - 1 - index }[event.key];
        if (offset === undefined) return;
        event.preventDefault();
        const next = (index + offset + this.tabs.length) % this.tabs.length;
        this.select(next);
        this.tabs[next].focus();
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
          tab.setAttribute('aria-selected', i === index);
          tab.setAttribute('tabindex', i === index ? '0' : '-1');
          if (panel) panel.classList.toggle('is-active', i === index);
        });
      }
    }
  );
}

if ('IntersectionObserver' in window) {
  const watchReels = () => {
    const reels = document.querySelectorAll('video.amara-ugc__media:not([data-observed])');
    if (!reels.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) target.play?.().catch(() => {});
          else target.pause?.();
        });
      },
      { threshold: 0.4 }
    );

    reels.forEach((reel) => {
      reel.dataset.observed = 'true';
      reel.autoplay = false;
      reel.pause();
      observer.observe(reel);
    });
  };

  document.addEventListener('DOMContentLoaded', watchReels);
  document.addEventListener('shopify:section:load', watchReels);
}

{
  // On mobile the sticky bar is the only add to cart, so its button submits the
  // page's own product form through the form attribute and then mirrors that
  // form's own button: label, availability and loading state, plus the price.
  // The body class is what lets the CSS hide the in-page button and quantity
  // selector, so a page whose bar never wired up keeps them.
  const stickyParts = () => {
    const button = document.querySelector('[data-amara-sticky-add]');
    const info = document.querySelector('product-info, .product__info-container');
    const form = info?.querySelector('form[data-type="add-to-cart-form"]');
    const submit = info?.querySelector('.product-form__submit');
    // getAttribute, not form.id: the form carries a hidden input named "id" for
    // the variant, and a form's named controls shadow the form's own properties,
    // so form.id is that input and the form attribute would point at nothing.
    const formId = form?.getAttribute('id');
    if (!button || !formId || !submit) return null;
    return { button, form, formId, submit, price: info.querySelector('[id^="price-"]') };
  };

  const syncStickyBar = () => {
    const parts = stickyParts();
    if (!parts) return;
    const { button, form, formId, submit, price } = parts;
    const loading = submit.classList.contains('loading');

    button.setAttribute('form', formId);
    button.toggleAttribute('disabled', submit.hasAttribute('disabled'));
    button.classList.toggle('loading', loading);
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);

    const label = button.querySelector('span');
    const submitLabel = submit.querySelector('span');
    if (label && submitLabel) label.textContent = submitLabel.textContent.trim();

    const stickyPrice = button.closest('.amara-sticky')?.querySelector('[data-amara-sticky-price]');
    if (price && stickyPrice && stickyPrice.innerHTML !== price.innerHTML) {
      stickyPrice.innerHTML = price.innerHTML;
    }

    if (!submit.dataset.amaraMirrored) {
      submit.dataset.amaraMirrored = 'true';
      const watch = { attributes: true, childList: true, subtree: true, characterData: true };
      const observer = new MutationObserver(syncStickyBar);
      observer.observe(submit, watch);
      if (price) observer.observe(price, watch);
    }

    // Belt and braces: a submit button with no form owner does nothing at all
    // on click, so if the form attribute ever fails to take, hand the click to
    // the in-page button instead -- hidden is still clickable. Guarded on
    // button.form, which is set the moment the attribute does resolve, so a
    // wired-up bar never submits twice.
    if (!button.dataset.amaraFallback) {
      button.dataset.amaraFallback = 'true';
      button.addEventListener('click', () => {
        if (button.form) return;
        stickyParts()?.submit.click();
      });
    }

    // Hide the in-page buttons only once this one really owns the form, so a
    // bar that failed to wire up leaves the page its own add to cart.
    if (button.form === form) document.body.classList.add('amara-sticky-ready');
  };

  syncStickyBar();
  document.addEventListener('DOMContentLoaded', syncStickyBar);
  document.addEventListener('shopify:section:load', syncStickyBar);
}

if (!customElements.get('amara-countdown')) {
  customElements.define(
    'amara-countdown',
    class AmaraCountdown extends HTMLElement {
      connectedCallback() {
        this.end = new Date(String(this.dataset.end || '').trim().replace(' ', 'T')).getTime();
        if (Number.isNaN(this.end)) {
          this.hidden = true;
          return;
        }

        this.fields = {
          days: this.querySelector('[data-countdown="days"]'),
          hours: this.querySelector('[data-countdown="hours"]'),
          minutes: this.querySelector('[data-countdown="minutes"]'),
          seconds: this.querySelector('[data-countdown="seconds"]'),
        };

        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      tick() {
        const remaining = this.end - Date.now();
        if (remaining <= 0) return this.finish();

        const seconds = Math.floor(remaining / 1000);
        const pad = (value) => String(value).padStart(2, '0');

        this.fields.days.textContent = pad(Math.floor(seconds / 86400));
        this.fields.hours.textContent = pad(Math.floor(seconds / 3600) % 24);
        this.fields.minutes.textContent = pad(Math.floor(seconds / 60) % 60);
        this.fields.seconds.textContent = pad(seconds % 60);
      }

      finish() {
        clearInterval(this.timer);
        const bar = this.closest('.amara-cdbar');

        if (this.dataset.onExpiry === 'message' && this.dataset.expiredText) {
          this.textContent = this.dataset.expiredText;
          this.classList.add('amara-cdbar__expired');
        } else if (bar) {
          bar.hidden = true;
        } else {
          this.hidden = true;
        }
      }
    }
  );
}

if (!customElements.get('amara-cart-timer')) {
  const STORAGE_KEY = 'amara:cart-timer-ends';

  customElements.define(
    'amara-cart-timer',
    class AmaraCartTimer extends HTMLElement {
      connectedCallback() {
        this.clock = this.querySelector('[data-carttimer-clock]');
        if (!this.clock) return;

        const minutes = Number(this.dataset.minutes) || 10;
        let end = Number(this.read());
        if (!end || end <= Date.now()) {
          end = Date.now() + minutes * 60000;
          this.write(end);
        }
        this.end = end;

        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      read() {
        try {
          return sessionStorage.getItem(STORAGE_KEY);
        } catch (error) {
          return null;
        }
      }

      write(value) {
        try {
          sessionStorage.setItem(STORAGE_KEY, String(value));
        } catch (error) {
        }
      }

      tick() {
        const remaining = this.end - Date.now();
        if (remaining <= 0) return this.finish();

        const seconds = Math.floor(remaining / 1000);
        const pad = (value) => String(value).padStart(2, '0');
        this.clock.textContent = `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
      }

      finish() {
        clearInterval(this.timer);
        this.classList.add('is-expired');
        if (this.dataset.expiredText) this.textContent = this.dataset.expiredText;
        else this.hidden = true;
      }
    }
  );
}

(() => {
  const phones = window.matchMedia('(max-width: 749px)');
  let groups = [];

  const setOpen = (group, isOpen) => {
    group.block.classList.toggle('is-open', isOpen);
    group.button.setAttribute('aria-expanded', String(isOpen));
    group.list.hidden = !isOpen;
  };

  const toggle = (group) => {
    const next = group.list.hidden;
    groups.forEach((other) => setOpen(other, other === group && next));
  };

  const build = (block, index) => {
    const heading = block.querySelector('.footer-block__heading');
    const list = block.querySelector('.footer-block__details-content');
    if (!heading || !list) return null;

    let button = heading.querySelector('.amara-footer-toggle');
    if (!button) {
      list.id = list.id || `AmaraFooterMenu-${index}`;
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'amara-footer-toggle';
      button.innerHTML = heading.innerHTML;
      button.setAttribute('aria-controls', list.id);

      const icon = document.createElement('span');
      icon.className = 'amara-footer-toggle__icon';
      icon.setAttribute('aria-hidden', 'true');
      button.appendChild(icon);

      heading.textContent = '';
      heading.appendChild(button);
    }

    const group = { block, button, list };
    button.addEventListener('click', () => toggle(group));
    return group;
  };

  const apply = () => {
    if (phones.matches) {
      groups.forEach((group) => setOpen(group, false));
    } else {
      groups.forEach((group) => {
        group.block.classList.remove('is-open');
        group.button.removeAttribute('aria-expanded');
        group.list.hidden = false;
      });
    }
  };

  const init = () => {
    groups = Array.from(document.querySelectorAll('.footer-block--menu')).map(build).filter(Boolean);
    apply();
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);

  if (phones.addEventListener) phones.addEventListener('change', apply);
  else phones.addListener(apply);
})();

if (!customElements.get('amara-video-popup')) {
  const STORAGE_KEY = 'amara:video-popup-closed';

  customElements.define(
    'amara-video-popup',
    class AmaraVideoPopup extends HTMLElement {
      connectedCallback() {
        if (this.dismissed) return;

        this.slides = Array.from(this.querySelectorAll('.amara-vpop__slide'));
        if (!this.slides.length) return;
        this.index = 0;

        this.querySelector('.amara-vpop__close')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });

        this.timer = setTimeout(() => this.reveal(), Number(this.dataset.delay) || 0);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        clearInterval(this.rotation);
      }

      videoAt(index) {
        return this.slides[index]?.querySelector('video');
      }

      get dismissed() {
        try {
          return sessionStorage.getItem(STORAGE_KEY) === '1';
        } catch (error) {
          return false;
        }
      }

      reveal() {
        this.hidden = false;

        const always = this.dataset.alwaysPlay === 'true';
        this.still = !always && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.play(this.index);

        const rotate = Number(this.dataset.rotate) || 0;
        if (this.slides.length > 1 && rotate > 0) {
          this.rotation = setInterval(() => this.advance(), rotate);
        }
      }

      advance() {
        const previous = this.index;
        this.index = (this.index + 1) % this.slides.length;

        this.slides[previous].classList.remove('is-active');
        this.slides[this.index].classList.add('is-active');

        const leaving = this.videoAt(previous);
        if (leaving) {
          leaving.pause();
          leaving.currentTime = 0;
        }

        this.play(this.index);
      }

      play(index) {
        const video = this.videoAt(index);
        if (!video || this.still) return;

        video.play?.().catch(() => {});
        video.addEventListener('loadeddata', () => video.play?.().catch(() => {}), { once: true });
      }

      close() {
        clearTimeout(this.timer);
        clearInterval(this.rotation);
        this.hidden = true;
        this.videoAt(this.index)?.pause?.();
        try {
          sessionStorage.setItem(STORAGE_KEY, '1');
        } catch (error) {
        }
      }
    }
  );
}

document.addEventListener(
  'click',
  (event) => {
    const summary = event.target.closest?.('.amara-faq__item > summary');
    if (!summary) return;

    const row = summary.parentElement;
    if (row.open) return;

    const group = row.closest('.amara-faq__cols') || row.parentElement;

    group.querySelectorAll('.amara-faq__item[open]').forEach((other) => {
      if (other !== row) other.open = false;
    });
  },
  true
);

if (!customElements.get('amara-slider')) {
  customElements.define(
    'amara-slider',
    class AmaraSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-slider-track]');
        this.dots = this.querySelector('[data-slider-dots]');
        this.slides = this.track ? Array.from(this.track.children) : [];
        if (this.slides.length < 2) {
          this.toggleAttribute('hidden-nav', true);
          return;
        }

        this.querySelectorAll('[data-slider-arrow]').forEach((button) => {
          button.addEventListener('click', () => {
            this.stop();
            this.go(this.index + Number(button.dataset.sliderArrow));
          });
        });

        this.track.addEventListener('scroll', () => this.sync(), { passive: true });
        this.addEventListener('pointerdown', () => this.stop());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.start());

        this.resize = new ResizeObserver(() => this.sync());
        this.resize.observe(this.track);
        this.sync();
        this.start();
      }

      disconnectedCallback() {
        this.pause();
        this.resize?.disconnect();
      }

      get scrollable() {
        return this.track.scrollWidth - this.track.clientWidth > 1;
      }

      get step() {
        return this.slides[1].offsetLeft - this.slides[0].offsetLeft || this.slides[0].offsetWidth;
      }

      // Number of distinct scroll stops: with 8 slides and 4 visible there are 5, not 8.
      get count() {
        if (!this.scrollable) return 1;
        return Math.ceil((this.track.scrollWidth - this.track.clientWidth - 1) / this.step) + 1;
      }

      get index() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.track.scrollLeft >= max - 2) return this.count - 1;
        return Math.round(this.track.scrollLeft / this.step);
      }

      buildDots() {
        if (!this.dots) return;
        const count = this.count;
        if (this.dots.childElementCount === count) return;
        this.dots.replaceChildren();
        for (let i = 0; i < count; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'amara-slider__dot';
          dot.setAttribute('aria-label', `${i + 1} / ${count}`);
          dot.addEventListener('click', () => {
            this.stop();
            this.go(i);
          });
          this.dots.appendChild(dot);
        }
      }

      go(target) {
        const count = this.count;
        const index = (target + count) % count;
        this.track.scrollTo({ left: index * this.step, behavior: 'smooth' });
      }

      sync() {
        this.buildDots();
        const active = this.index;
        this.toggleAttribute('hidden-nav', !this.scrollable);
        this.querySelectorAll('.amara-slider__dot').forEach((dot, i) => {
          dot.setAttribute('aria-current', i === active);
        });
        if (!this.scrollable) this.pause();
      }

      start() {
        if (this.stopped || this.timer || this.dataset.autoplay !== 'true' || !this.scrollable) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.timer = setInterval(() => this.go(this.index + 1), Number(this.dataset.speed) || 5000);
      }

      pause() {
        clearInterval(this.timer);
        this.timer = null;
      }

      stop() {
        this.stopped = true;
        this.pause();
      }
    }
  );
}

if (!customElements.get('amara-glossary')) {
  customElements.define(
    'amara-glossary',
    class AmaraGlossary extends HTMLElement {
      connectedCallback() {
        this.drawer = this.querySelector('dialog');
        if (!this.drawer) return;

        this.items = Array.from(this.querySelectorAll('[data-gloss-item]'));
        this.entries = Array.from(this.drawer.querySelectorAll('[data-gloss-entry]'));
        this.scroller = this.drawer.querySelector('[data-gloss-scroll]');
        this.nav = this.drawer.querySelector('[data-gloss-nav]');
        this.steps = Array.from(this.drawer.querySelectorAll('[data-gloss-step]'));

        this.addEventListener('click', (event) => this.onClick(event));
        this.drawer.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });
        // Browsers may still force the dialog shut (a repeated Escape), skipping finishClose.
        this.drawer.addEventListener('close', () => {
          clearTimeout(this.closeTimer);
          this.drawer.classList.remove('is-closing');
          this.cleanup();
        });

        // Theme editor: selecting an ingredient block opens its drawer so the content can be checked.
        this.addEventListener('shopify:block:select', (event) => {
          const item = event.target.closest?.('[data-gloss-item]');
          if (item) this.open(this.items.indexOf(item));
        });
        this.addEventListener('shopify:block:deselect', () => this.close(true));

        // Adding to cart opens the cart drawer, which a modal dialog would cover, so step aside first.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.onCartUpdate());
        }

        const linked = this.entries.findIndex((entry) => `#${entry.id}` === window.location.hash);
        if (linked > -1) this.open(linked);
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        clearTimeout(this.closeTimer);
        if (this.isOpen) document.body.classList.remove('overflow-hidden');
      }

      onClick(event) {
        const filter = event.target.closest('[data-gloss-filter]');
        if (filter) return this.filter(filter);

        const card = event.target.closest('[data-gloss-open]');
        if (card) return this.open(this.items.indexOf(card.closest('[data-gloss-item]')));

        const step = event.target.closest('[data-gloss-step]');
        if (step) return this.step(Number(step.dataset.glossStep));

        // A click on the dialog element itself landed on the backdrop.
        if (event.target === this.drawer || event.target.closest('[data-gloss-close]')) this.close();
      }

      filter(button) {
        const group = button.dataset.glossFilter;
        this.querySelectorAll('[data-gloss-filter]').forEach((chip) => {
          chip.setAttribute('aria-pressed', String(chip === button));
        });
        this.items.forEach((item) => {
          item.hidden = group !== 'all' && !item.dataset.glossGroups.split(' ').includes(group);
        });
      }

      get visible() {
        return this.items.map((item, index) => (item.hidden ? -1 : index)).filter((index) => index > -1);
      }

      open(index) {
        const entry = this.entries[index];
        if (!entry) return;

        this.current = index;
        this.entries.forEach((other) => {
          other.hidden = other !== entry;
        });
        this.drawer.setAttribute('aria-labelledby', `${entry.id}-title`);
        this.scroller.scrollTop = 0;
        this.updateSteps();

        clearTimeout(this.closeTimer);
        this.drawer.classList.remove('is-closing');
        if (!this.drawer.open) {
          this.drawer.showModal();
          this.isOpen = true;
          document.body.classList.add('overflow-hidden');
        }

        history.replaceState(history.state, '', `#${entry.id}`);
      }

      step(direction) {
        const visible = this.visible;
        if (!visible.length) return;
        const position = visible.indexOf(this.current);
        this.open(visible[(position + direction + visible.length) % visible.length]);
      }

      updateSteps() {
        const visible = this.visible;
        const position = visible.indexOf(this.current);
        this.nav.hidden = visible.length < 2;

        // Each arrow is named after the ingredient it leads to.
        this.steps.forEach((button) => {
          const target = visible[(position + Number(button.dataset.glossStep) + visible.length) % visible.length];
          const name = this.entries[target]?.querySelector('.amara-gloss__title')?.textContent.trim();
          if (name) button.setAttribute('aria-label', name);
        });
      }

      close(immediate = false) {
        if (!this.drawer.open) return;
        clearTimeout(this.closeTimer);

        if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return this.finishClose();

        this.drawer.classList.add('is-closing');
        this.closeTimer = setTimeout(() => this.finishClose(), 280);
      }

      // Cleanup runs right after close() rather than from the close event, which fires a task
      // later, by when the cart drawer may already hold focus and the body scroll lock.
      finishClose() {
        this.drawer.classList.remove('is-closing');
        this.drawer.close();
        this.cleanup();
      }

      cleanup() {
        if (!this.isOpen) return;
        this.isOpen = false;
        document.body.classList.remove('overflow-hidden');
        this.cardFor(this.current)?.focus();
        history.replaceState(history.state, '', window.location.pathname + window.location.search);
      }

      cardFor(index) {
        return this.items[index]?.querySelector('[data-gloss-open]');
      }

      onCartUpdate() {
        if (!this.drawer.open) return;
        this.close(true);
        // The cart drawer returns focus here when it closes, not to a button inside the closed dialog.
        document.querySelector('cart-drawer')?.setActiveElement?.(this.cardFor(this.current));
      }
    }
  );
}
if (!customElements.get('amara-before-after')) {
  customElements.define(
    'amara-before-after',
    class AmaraBeforeAfter extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('input[type="range"]');
        if (!this.range) return;

        // The hidden range input carries keyboard and screen reader use; pointers drag the frame.
        this.range.addEventListener('input', () => this.set(this.range.value));

        this.addEventListener('pointerdown', (event) => {
          if (event.button !== 0) return;
          this.dragging = true;
          this.setPointerCapture?.(event.pointerId);
          // A touch may turn out to be a page scroll, so only a mouse jumps straight to the press point.
          if (event.pointerType === 'mouse') this.follow(event);
        });
        this.addEventListener('pointermove', (event) => {
          if (this.dragging) this.follow(event);
        });
        ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) => {
          this.addEventListener(type, () => {
            this.dragging = false;
          });
        });
      }

      follow(event) {
        const frame = this.getBoundingClientRect();
        const value = Math.min(100, Math.max(0, ((event.clientX - frame.left) / frame.width) * 100));
        this.range.value = value;
        this.set(value);
      }

      set(value) {
        this.style.setProperty('--ba-pos', `${value}%`);
      }
    }
  );
}

if (!customElements.get('amara-clinical')) {
  customElements.define(
    'amara-clinical',
    class AmaraClinical extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('[data-clin-item]'));

        this.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-clin-filter]');
          if (chip) this.filter(chip);
        });
      }

      filter(chip) {
        const concern = chip.dataset.clinFilter;
        this.querySelectorAll('[data-clin-filter]').forEach((other) => {
          other.setAttribute('aria-pressed', String(other === chip));
        });
        this.items.forEach((item) => {
          item.hidden = concern !== 'all' && !item.dataset.clinConcerns.split(' ').includes(concern);
        });
      }
    }
  );
}

// Result product cards (snippets/amara-result-product.liquid): keep the price in step with the size picked.
document.addEventListener('change', (event) => {
  const select = event.target.closest?.('[data-rp-variant]');
  const option = select?.selectedOptions[0];
  const card = select?.closest('[data-rp]');
  if (!option || !card) return;

  card.querySelector('[data-rp-price]').textContent = option.dataset.price;
  const compare = card.querySelector('[data-rp-compare]');
  compare.textContent = option.dataset.compare;
  compare.hidden = !option.dataset.compare;
});